import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { adminAPI } from '../../lib/api';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import {
  Users,
  Home,
  DoorOpen,
  FileText,
  TrendingUp,
  ArrowRight,
  Crown,
  Building2,
  DollarSign,
} from 'lucide-react';
import { useI18n } from '../../context/I18nContext';

const AdminDashboard = () => {
  const { t } = useI18n();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const response = await adminAPI.getStats();
      setStats(response.data);
    } catch (error) {
      toast.error('Failed to load platform stats');
    } finally {
      setLoading(false);
    }
  };

  if (loading || !stats) {
    return (
      <DashboardLayout type="admin" title={t('admin.title')}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-32 bg-card animate-pulse" />
          ))}
        </div>
      </DashboardLayout>
    );
  }

  const metricCards = [
    { title: 'Monthly Recurring Revenue', value: `$${stats.mrr?.toLocaleString() || '0'}`, icon: DollarSign, color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
    { title: 'Active Landlords', value: stats.total_landlords, icon: Crown, color: 'text-gold', bg: 'bg-gold/10', link: '/admin/landlords' },
    { title: 'Total Tenants', value: stats.total_tenants, icon: Users, color: 'text-blue-400', bg: 'bg-blue-500/10' },
    { title: 'Organisations', value: stats.total_organisations, icon: Building2, color: 'text-purple-400', bg: 'bg-purple-500/10', link: '/admin/organisations' },
  ];

  const secondaryCards = [
    { title: 'Properties', value: stats.total_properties, icon: Home, color: 'text-amber-400' },
    { title: 'Units', value: stats.total_units, icon: DoorOpen, color: 'text-teal-400' },
    { title: 'Active Leases', value: stats.active_leases, icon: FileText, color: 'text-indigo-400' },
  ];

  return (
    <DashboardLayout type="admin" title={t('admin.title')}>
      <div className="space-y-8" data-testid="admin-dashboard">
        <div>
          <h2 className="font-serif text-2xl mb-1">
            Super Admin <span className="text-gold">Dashboard</span>
          </h2>
          <p className="text-muted-foreground">Monitor and manage the Realty JAG platform</p>
        </div>

        {/* Primary Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {metricCards.map((stat, index) => {
            const Wrapper = stat.link ? Link : 'div';
            return (
              <Wrapper key={stat.title} to={stat.link || '#'}>
                <Card
                  className="bg-card border-border hover:border-gold/30 transition-all duration-300 card-animate"
                  style={{ animationDelay: `${index * 0.08}s` }}
                  data-testid={`metric-${stat.title.toLowerCase().replace(/ /g, '-')}`}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">{stat.title}</p>
                        <p className="text-3xl font-semibold">{stat.value}</p>
                      </div>
                      <div className={`w-10 h-10 ${stat.bg} flex items-center justify-center`}>
                        <stat.icon className={`w-5 h-5 ${stat.color}`} />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Wrapper>
            );
          })}
        </div>

        {/* Secondary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {secondaryCards.map((stat, index) => (
            <Card
              key={stat.title}
              className="bg-card border-border card-animate"
              style={{ animationDelay: `${(index + 4) * 0.08}s` }}
            >
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-wider text-muted-foreground">{stat.title}</p>
                    <p className="text-2xl font-semibold mt-1">{stat.value}</p>
                  </div>
                  <stat.icon className={`w-6 h-6 ${stat.color} opacity-50`} />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Quick Actions */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="font-serif">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Link
                to="/admin/organisations"
                className="flex items-center justify-between p-4 bg-background hover:bg-gold/5 border border-border hover:border-gold/30 transition-all duration-300"
                data-testid="manage-organisations-link"
              >
                <div className="flex items-center gap-3">
                  <Building2 className="w-5 h-5 text-gold" />
                  <span>Manage Organisations</span>
                </div>
                <ArrowRight className="w-4 h-4 text-muted-foreground" />
              </Link>
              <Link
                to="/admin/landlords"
                className="flex items-center justify-between p-4 bg-background hover:bg-gold/5 border border-border hover:border-gold/30 transition-all duration-300"
                data-testid="manage-landlords-link"
              >
                <div className="flex items-center gap-3">
                  <Crown className="w-5 h-5 text-gold" />
                  <span>Manage Landlords</span>
                </div>
                <ArrowRight className="w-4 h-4 text-muted-foreground" />
              </Link>
              <Link
                to="/admin/payments"
                className="flex items-center justify-between p-4 bg-background hover:bg-gold/5 border border-border hover:border-gold/30 transition-all duration-300"
                data-testid="platform-payments-link"
              >
                <div className="flex items-center gap-3">
                  <DollarSign className="w-5 h-5 text-gold" />
                  <span>Platform Payments</span>
                </div>
                <ArrowRight className="w-4 h-4 text-muted-foreground" />
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* Platform Health */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="font-serif">Platform Health</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Occupancy Rate</p>
                <p className="text-2xl font-semibold">
                  {stats.total_units > 0 ? Math.round((stats.active_leases / stats.total_units) * 100) : 0}%
                </p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Avg Units/Landlord</p>
                <p className="text-2xl font-semibold">
                  {stats.total_landlords > 0 ? Math.round(stats.total_units / stats.total_landlords) : 0}
                </p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Avg Tenants/Org</p>
                <p className="text-2xl font-semibold">
                  {stats.total_organisations > 0 ? Math.round(stats.total_tenants / stats.total_organisations) : 0}
                </p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Platform Status</p>
                <span className="px-2 py-0.5 text-xs uppercase tracking-wider bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                  Healthy
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default AdminDashboard;
